import { useState } from "react";
import { format, parseISO } from "date-fns";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import {
  BarChart3,
  Play,
  Loader2,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Calendar,
  Info,
} from "lucide-react";
import {
  useBehaviorScheduleSettings,
  useUpdateBehaviorScheduleSettings,
  useBehaviorManualSnapshotRun,
  useBehaviorHistoricalRebuild,
} from "@/hooks/use-behavior-snapshots";

const settingsFormSchema = z.object({
  isEnabled: z.boolean(),
  runTimeLocal: z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, "Must be HH:MM format"),
});

type SettingsFormData = z.infer<typeof settingsFormSchema>;

function formatTimestamp(timestamp: string | null): string {
  if (!timestamp) return "Never";
  try {
    return format(parseISO(timestamp), "MMM d, yyyy h:mm a");
  } catch {
    return timestamp;
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "N/A";
  try {
    return format(parseISO(dateStr), "MMM d, yyyy");
  } catch {
    return dateStr;
  }
}

export function BehaviorSnapshotSettingsCard() {
  const { toast } = useToast();
  const [showForceConfirm, setShowForceConfirm] = useState(false);
  const [showRebuildConfirm, setShowRebuildConfirm] = useState(false);
  const [rebuildDays, setRebuildDays] = useState(60);

  const {
    data: settings,
    isLoading,
    isError,
    refetch,
  } = useBehaviorScheduleSettings();

  const updateMutation = useUpdateBehaviorScheduleSettings();
  const manualRunMutation = useBehaviorManualSnapshotRun();
  const historicalRebuildMutation = useBehaviorHistoricalRebuild();

  const form = useForm<SettingsFormData>({
    resolver: zodResolver(settingsFormSchema),
    defaultValues: {
      isEnabled: true,
      runTimeLocal: "00:15",
    },
  });

  if (settings && !form.formState.isDirty) {
    const currentEnabled = form.getValues("isEnabled");
    const currentRunTime = form.getValues("runTimeLocal");
    if (currentEnabled !== settings.isEnabled || currentRunTime !== settings.runTimeLocal) {
      form.reset({
        isEnabled: settings.isEnabled,
        runTimeLocal: settings.runTimeLocal,
      });
    }
  }

  const onSubmit = async (data: SettingsFormData) => {
    try {
      await updateMutation.mutateAsync(data);
      toast({
        title: "Settings saved",
        description: "Behavior snapshot schedule settings updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Failed to save settings",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const handleManualRun = async (force: boolean = false) => {
    try {
      const result = await manualRunMutation.mutateAsync({ force });
      if (result.noOp) {
        toast({
          title: "Snapshot already exists",
          description: `A behavior snapshot for ${formatDate(result.snapshotDate || null)} already exists. No changes made.`,
        });
      } else {
        toast({
          title: "Snapshot generated",
          description: `Behavior snapshot for ${formatDate(result.snapshotDate || null)} created successfully (${result.rowsWritten} rows).`,
        });
      }
      setShowForceConfirm(false);
    } catch (error) {
      toast({
        title: "Failed to generate snapshot",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const handleHistoricalRebuild = async () => {
    try {
      const result = await historicalRebuildMutation.mutateAsync({ days: rebuildDays });
      if (result.ok) {
        toast({
          title: "Historical rebuild complete",
          description: `Rebuilt ${result.datesProcessed} dates (${result.rowsWritten} total rows).`,
        });
      } else {
        toast({
          title: "Rebuild completed with errors",
          description: `Rebuilt ${result.datesProcessed} dates with ${result.errors.length} errors. Check logs for details.`,
          variant: "destructive",
        });
      }
      setShowRebuildConfirm(false);
    } catch (error) {
      toast({
        title: "Failed to rebuild snapshots",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <Card data-testid="card-behavior-snapshot-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Behavior Score Snapshot Schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-4 w-64" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card data-testid="card-behavior-snapshot-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Behavior Score Snapshot Schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
            <p className="text-muted-foreground mb-4">Failed to load settings</p>
            <Button variant="outline" onClick={() => refetch()} data-testid="button-retry-behavior-settings">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="card-behavior-snapshot-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Behavior Score Snapshot Schedule
          </CardTitle>
          <CardDescription>
            Configure when daily behavior score snapshots are automatically generated.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="isEnabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Automatic Snapshots</FormLabel>
                      <FormDescription>
                        Enable or disable automatic daily behavior score snapshot generation.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="switch-behavior-snapshot-enabled"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="runTimeLocal"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Daily Run Time (24h format)</FormLabel>
                      <FormControl>
                        <Input
                          type="time"
                          {...field}
                          data-testid="input-behavior-run-time"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <div className="flex items-center gap-2 h-10 px-3 rounded-md border bg-muted/50 text-sm">
                    <span data-testid="text-behavior-timezone">America/New_York</span>
                    <span className="text-xs text-muted-foreground">(read-only)</span>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={updateMutation.isPending || !form.formState.isDirty}
                data-testid="button-save-behavior-settings"
              >
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Settings
              </Button>
            </form>
          </Form>

          <div className="border-t pt-6">
            <h4 className="font-medium mb-4">Last Run Information</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Last run:</span>
                <span data-testid="text-behavior-last-run-at">{formatTimestamp(settings?.lastRunAt || null)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Snapshot date:</span>
                <span data-testid="text-behavior-last-snapshot-date">
                  {settings?.lastRunSnapshotDate ? formatDate(settings.lastRunSnapshotDate) : "N/A"}
                </span>
              </div>
            </div>

            {settings?.lastError && (
              <Alert variant="destructive" className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Last Error</AlertTitle>
                <AlertDescription data-testid="text-behavior-last-error">
                  {settings.lastError}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="border-t pt-6">
            <h4 className="font-medium mb-2">Manual Run</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Manually generate a behavior score snapshot for the latest date with canonical interactions.
              Use "Run Now" to generate only if no snapshot exists, or "Force Overwrite" to replace any existing snapshot.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => handleManualRun(false)}
                disabled={manualRunMutation.isPending}
                data-testid="button-behavior-manual-run"
              >
                {manualRunMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Play className="h-4 w-4 mr-2" />
                Run Now
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowForceConfirm(true)}
                disabled={manualRunMutation.isPending}
                data-testid="button-behavior-manual-run-force"
              >
                Force Overwrite
              </Button>
            </div>
          </div>

          <div className="border-t pt-6">
            <h4 className="font-medium mb-2">Rebuild Historical</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Regenerate behavior score snapshots for multiple past dates. This overwrites existing snapshots.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-2">
                <Label htmlFor="behavior-rebuild-days">Days back</Label>
                <Input
                  id="behavior-rebuild-days"
                  type="number"
                  min={1}
                  max={365}
                  value={rebuildDays}
                  onChange={(e) => setRebuildDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 60)))}
                  className="w-24"
                  data-testid="input-behavior-rebuild-days"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowRebuildConfirm(true)}
                disabled={historicalRebuildMutation.isPending}
                data-testid="button-behavior-rebuild-historical"
              >
                {historicalRebuildMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <RefreshCw className="h-4 w-4 mr-2" />
                Rebuild Historical
              </Button>
            </div>
            {historicalRebuildMutation.isPending && (
              <p className="text-sm text-muted-foreground mt-3">
                Rebuilding snapshots... This may take several minutes for large date ranges.
              </p>
            )}
          </div>

          <Alert className="mt-4 border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-blue-700 dark:text-blue-300 text-sm">
              Behavior score snapshots aggregate canonical behavior interactions by score code and location.
              The scheduled run ensures daily data collection for analytics dashboards.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <Dialog open={showForceConfirm} onOpenChange={setShowForceConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Overwrite</DialogTitle>
            <DialogDescription>
              This will overwrite any existing behavior score snapshot for today's date.
              Are you sure you want to proceed?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForceConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleManualRun(true)}
              disabled={manualRunMutation.isPending}
              data-testid="button-confirm-behavior-force"
            >
              {manualRunMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Overwrite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showRebuildConfirm} onOpenChange={setShowRebuildConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Historical Rebuild</DialogTitle>
            <DialogDescription>
              This will regenerate behavior score snapshots for the last {rebuildDays} days,
              overwriting any existing snapshots. This operation may take several minutes.
              Are you sure you want to proceed?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRebuildConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleHistoricalRebuild}
              disabled={historicalRebuildMutation.isPending}
              data-testid="button-confirm-behavior-rebuild"
            >
              {historicalRebuildMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Rebuild {rebuildDays} Days
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
